import styled, { css } from 'styled-components';
import { Link } from 'gatsby-plugin-react-intl';
import { themeGet } from '@styled-system/theme-get';

import { ButtonStyles, IconStyles } from '../Button/styles';

// * Links can be styled just like our button components by adding the classes cta & either primary or secondary

export const LinkStyles = css`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: color 0.3s ease;

  // * Sets size of default icon used to indicate link opens in an external tab. Gets current color of text to ensure svg matches color.
  & > svg {
    color: currentColor;
    height: 14px;
    width: 14px;
    margin-left: 5px;
    flex: 0 0 auto;
    transition: all 0.2s ease;

    /* IE 11 fix */
    @media all and (-ms-high-contrast: none) {
      width: 14px;
    }
  }

  & > span {
    padding-bottom: 1px;
    border-bottom: 1px solid currentColor;
    font-weight: ${themeGet('fontWeights.medium')};
  }

  &:hover {
    color: ${themeGet('colors.pc-red', '#E1251B')};

    & > span {
      border-bottom: 1px solid ${themeGet('colors.pc-red', '#E1251B')};
    }

    & > svg {
      color: ${themeGet('colors.pc-red', '#E1251B')};
    }
  }

  &.rich-text-link {
    &:hover {
      & > svg {
        transform: translate(2px, -2px);
      }
    }
  }

  &.unstyled-link {
    display: unset;

    & > svg {
      display: none;
    }

    & > span {
      padding-bottom: 0;
      border-bottom: none;
      font-weight: inherit;
    }

    &:before,
    &:after {
      display: none;
    }

    &:hover {
      & > span {
        border: none;
      }
    }
    &.nav-tab {
      display: block;
      width: 100%;

      &.btn-border,
      &.btn-primary,
      &.primary-inverse & {
        width: fit-content;
      }
    }
  }

  &.nav-link {
    & > svg {
      display: none;
    }

    & > span {
      padding-bottom: 0;
      border-bottom: none;
      font-weight: inherit;
    }

    &:hover,
    &:focus {
      color: ${themeGet('colors.main-text')};
      & > svg {
        color: ${themeGet('colors.main-text')};
      }
    }
  }

  &.faq-item-link {
    width: 100%;
    justify-content: space-between;

    & > span {
      padding-bottom: 0;
      border-bottom: none;
      font-weight: inherit;
    }
  }

  &.breadcrumb-link {
    display: unset;

    & > svg {
      display: none;
    }

    & > span {
      padding-bottom: 0;
      border-bottom: none;
      font-weight: inherit;
    }

    &:before,
    &:after {
      display: none;
    }

    &:hover {
      & > span {
        border-bottom: 1px solid ${themeGet('colors.pc-red', '#E1251B')};
      }
    }
  }

  ${ButtonStyles}
`;

export const InternalStyledLink = styled(Link)`
  ${LinkStyles}

  & > svg {
    vertical-align: middle;
  }

  &:hover,
  &:focus {
    & > svg {
      color: currentColor;
      transform: translateX(4px);
      transition: transform 0.5s ease;
    }
  }

  &.rich-text-link {
    border: none;
  }
`;

export const ExternalStyledLink = styled.a`
  ${LinkStyles}
  ${IconStyles}

  display: inline-flex;
  align-items: center;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* IE10+ CSS styles go here */
    & > span {
      white-space: nowrap;
    }
  }

  ${({ icon }) => (icon ? `${IconStyles}` : '')};

  &:hover,
  &:focus {
    & > svg {
      transform: translate(2px, -2px);
    }
    > span {
      > svg {
        transform: translate(2px, -2px);
      }
    }
  }

  // * If placement has been selected as either (:before) or (:after) and an icon has been added in the CMS, set content to display in the correct position and use icon as a background image on the selected pseudo element. NOTE - placement is set to false by default.
  &::after {
    content: ${({ placement, icon }) =>
      placement === 'false' && icon ? '' : `none`};
    mask: ${props => props.icon && `url(${props.icon})`};
    -webkit-mask: ${props => props.icon && `url(${props.icon})`};
    mask-size: cover;
    -webkit-mask-size: cover;
    background-color: currentColor;
    background-color: 2px solid currentColor;
  }

  &:hover,
  &:focus {
    &::after {
      content: ${({ placement, icon }) =>
        placement === 'false' && icon ? '' : `none`};
      mask: ${props =>
        // eslint-disable-next-line no-nested-ternary
        props.hovericon
          ? `url(${props.hovericon})`
          : props.icon
            ? `url(${props.icon})`
            : 'none'};
      -webkit-mask: ${props =>
        // eslint-disable-next-line no-nested-ternary
        props.hovericon
          ? `url(${props.hovericon})`
          : props.icon
            ? `url(${props.icon})`
            : 'none'};
      -webkit-mask-size: cover;
    }
  }

  &::before {
    content: ${({ placement, icon }) =>
      placement === 'true' && icon ? '' : `none`};
    mask: ${props => props.icon && `url(${props.icon})`};
    -webkit-mask: ${props => props.icon && `url(${props.icon})`};
    mask-size: cover;
    -webkit-mask-size: cover;
    background-color: currentColor;
  }

  &:hover,
  &:focus {
    &::before {
      content: ${({ placement, icon }) =>
        placement === 'true' && icon ? '' : `none`};
      mask: ${props =>
        // eslint-disable-next-line no-nested-ternary
        props.hovericon
          ? `url(${props.hovericon})`
          : props.icon
            ? `url(${props.icon})`
            : 'none'};
      -webkit-mask: ${props =>
        // eslint-disable-next-line no-nested-ternary
        props.hovericon
          ? `url(${props.hovericon})`
          : props.icon
            ? `url(${props.icon})`
            : 'none'};
    }
  }
`;
